import React, { useEffect, useState } from 'react';

type PageToken = number | 'ellipsis';

interface TablePaginationProps {
  total: number;
  page: number;
  pageCount: number;
  pageSize: number;
  onPageChange: (nextPage: number) => void;
  onPageSizeChange: (nextPageSize: number) => void;
  pageSizeOptions?: number[];
}

const defaultPageSizeOptions = [10, 20, 50];

const getVisiblePages = (page: number, pageCount: number): PageToken[] => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages: PageToken[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);

  if (start > 2) {
    pages.push('ellipsis');
  }

  for (let value = start; value <= end; value += 1) {
    pages.push(value);
  }

  if (end < pageCount - 1) {
    pages.push('ellipsis');
  }

  pages.push(pageCount);
  return pages;
};

const TablePagination: React.FC<TablePaginationProps> = ({
  total,
  page,
  pageCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = defaultPageSizeOptions
}) => {
  const [jumpValue, setJumpValue] = useState(`${page}`);
  const visiblePages = getVisiblePages(page, pageCount);

  useEffect(() => {
    setJumpValue(`${page}`);
  }, [page]);

  const handleJump = () => {
    const target = Number.parseInt(jumpValue, 10);
    if (Number.isNaN(target)) {
      setJumpValue(`${page}`);
      return;
    }
    onPageChange(target);
  };

  return (
    <div className="table-footer">
      <div className="table-summary">
        共 <span className="table-summary-number">{total}</span> 条记录
        {' '}
        第 <span className="table-summary-number">{page}</span> / <span className="table-summary-number">{pageCount}</span> 页
      </div>
      <div className="table-pagination">
        <select
          className="page-size"
          value={`${pageSize}`}
          onChange={event => onPageSizeChange(Number.parseInt(event.target.value, 10))}
        >
          {pageSizeOptions.map(option => (
            <option key={option} value={option}>{option}条/页</option>
          ))}
        </select>
        <div className="pager">
          <button
            type="button"
            className="pager-btn"
            aria-label="上一页"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            ‹
          </button>
          {visiblePages.map((token, index) =>
            token === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="pager-ellipsis">...</span>
            ) : (
              <button
                key={token}
                type="button"
                className={`pager-btn ${token === page ? 'active' : ''}`}
                onClick={() => onPageChange(token)}
              >
                {token}
              </button>
            )
          )}
          <button
            type="button"
            className="pager-btn"
            aria-label="下一页"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
          >
            ›
          </button>
        </div>
        <div className="page-jump">
          <span>前往</span>
          <input
            type="text"
            value={jumpValue}
            onChange={event => setJumpValue(event.target.value)}
            onBlur={handleJump}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                handleJump();
              }
            }}
          />
          <span>页</span>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;

